import type { PublicationTypeDto } from '@/api/ApiClient';
import type { FieldValidation } from '@/lib/utils/submitValidation';

function hasHtmlContent(value?: string): boolean {
  if (!value) return false;
  const stripped = value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
  return stripped.length > 0;
}

/**
 * Shared publication submit-validation fields used by both details and dialog flows.
 * Keep required-field semantics in one place to avoid drift.
 */
export function buildPublicationSubmitValidationFields(input: {
  title?: string;
  authors?: string;
  journal?: string;
  cite?: string;
  doi?: string;
  type?: PublicationTypeDto | string;
  year?: number;
  requireMainScienceDriver?: boolean;
  selectedScienceDriverIds?: number[];
}): FieldValidation[] {
  const fields: FieldValidation[] = [
    { key: 'title', label: 'Title', hasValue: hasHtmlContent(input.title), value: input.title },
    { key: 'authors', label: 'Authors', hasValue: hasHtmlContent(input.authors), value: input.authors },
    { key: 'journal', label: 'Journal', hasValue: Boolean(input.journal?.trim()), value: input.journal },
    { key: 'cite', label: 'Citation', hasValue: hasHtmlContent(input.cite), value: input.cite },
    { key: 'doi', label: 'DOI', hasValue: Boolean(input.doi?.trim()), value: input.doi },
    { key: 'type', label: 'Type', hasValue: Boolean(input.type), value: input.type },
    {
      key: 'year',
      label: 'Year',
      hasValue: input.year !== undefined && input.year !== null,
      value: input.year,
      validator: (val: number | undefined) => {
        if (val === undefined || val === null) return 'Year is required';
        if (val < 2000 || val > 2199) return 'Year must be between 2000 and 2199';
        return true;
      },
    },
  ];

  if (input.requireMainScienceDriver) {
    fields.push({
      key: 'mainScienceDriver',
      label: 'Main Science Driver',
      hasValue: (input.selectedScienceDriverIds?.length || 0) > 0,
      value: input.selectedScienceDriverIds,
    });
  }

  return fields;
}
